/* oxlint-disable jsx-a11y/click-events-have-key-events */
// https://www.figma.com/file/1o5fuZmMGpwrEhL0BLLWMO/AQX-Modal-Component?node-id=3%3A9775&t=5ITrZfIn6EWpf5vZ-0

'use client';

import cx from 'classnames';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { useBodyClass } from '@flipster/react/misc/browser/body-class';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';

import CloseIcon from '../assets/svgs/close.svg';
import styles from './drawer.module.scss';

interface Props {
  title?: ReactNode;
  isShow: boolean;
  onClose?: () => void;
  onExit?: () => void;
  position: 'bottom' | 'right';
  round?: boolean;
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  noPadding?: boolean;
  isFull?: boolean;
}

const animationMapper: Record<Props['position'], Record<'initial' | 'animate', Record<string, string>>> = {
  bottom: {
    initial: {
      transform: 'translateY(100%)',
    },
    animate: {
      transform: 'translateY(0)',
    },
  },
  right: {
    initial: {
      transform: 'translateX(100%)',
    },
    animate: {
      transform: 'translateX(0)',
    },
  },
};

export const Drawer = ({
  title,
  isShow,
  onClose,
  onExit,
  position,
  round = true,
  className,
  contentClassName,
  children,
  noPadding,
  isFull,
}: Props) => {
  const mounted = useMounted();
  const { isDesktop } = useResponsive();

  useBodyClass('drawer', !isDesktop && isShow);

  if (!mounted || isDesktop) return null;
  return createPortal(
    <AnimatePresence onExitComplete={onExit}>
      {isShow && (
        <aside className={cx(styles.wrapper, styles[position], { [styles.isFull]: isFull })}>
          <div className={styles.overlay} role='button' aria-label='drawer-overlay' tabIndex={-1} onClick={onClose} />
          <m.div
            className={cx(styles.drawer, className, { [styles.round]: round })}
            initial={animationMapper[position].initial}
            animate={{ transition: { ease: 'easeOut', duration: 0.2 }, ...animationMapper[position].animate }}
            exit={{ transition: { ease: 'easeIn', duration: 0.2 }, ...animationMapper[position].initial }}
            layout
          >
            {onClose && (
              <div className={styles.close}>
                <button type='button' aria-label='close' onClick={onClose} className={styles.closeButton}>
                  <CloseIcon />
                </button>
              </div>
            )}
            {title && (
              <header className={styles.header}>
                <h1 className={styles.title}>{title}</h1>
              </header>
            )}
            <div className={cx(styles.content, contentClassName, { [styles.noPadding]: noPadding })}>{children}</div>
          </m.div>
        </aside>
      )}
    </AnimatePresence>,
    document.querySelector('#modal') as HTMLDivElement,
  );
};
